import { z } from 'zod'
import { nullableCalendarDaySchema } from '../validators/calendar-day.validator.js'
import { roleSchema } from '../enums/role.enum.js'
import { userStatusSchema } from '../enums/user-status.enum.js'

/** Where a person's last invitation stands before a conversion run. */
const INVITATION_STANDINGS = ['never_sent', 'lapsed', 'still_valid'] as const
export const invitationStandingSchema = z.enum(INVITATION_STANDINGS)
export type InvitationStanding = z.infer<typeof invitationStandingSchema>

/**
 * What a conversion run will do with one person. The planner and the report
 * both use this, so a new outcome is one list.
 */
const CONVERSION_OUTCOMES = ['convert', 'already_vendor', 'blocked'] as const
export const conversionOutcomeSchema = z.enum(CONVERSION_OUTCOMES)
export type ConversionOutcome = z.infer<typeof conversionOutcomeSchema>

/** An event a person is a client on, which gains them as its planner. */
export const conversionEventDtoSchema = z.object({
  eventId: z.string().uuid(),
  eventName: z.string(),
  eventDate: nullableCalendarDaySchema,
  venueName: z.string(),
  organizationName: z.string(),
})

/**
 * The invitation a person who has not signed in gets, once, however many
 * events they are on (decision 34): the event it names and its venue, and how
 * their last invitation stands today.
 */
export const conversionInvitationDtoSchema = z.object({
  eventName: z.string(),
  venueName: z.string(),
  /** Their invitation as it stands before the run. */
  today: invitationStandingSchema,
  /** After an applied run: whether it went. Null in a dry run. */
  sent: z.boolean().nullable(),
  /** Why it did not go, when it did not. */
  error: z.string().nullable(),
})

/**
 * One row of the list, as the run found it and what it did or would do.
 *
 * - `convert` — converted, or would be in a dry run.
 * - `already_vendor` — converted before: nothing to do, and nothing done.
 * - `blocked` — something stops it; `problems` says what. An applied run with
 *   any blocked row writes nothing at all.
 */
export const conversionPersonDtoSchema = z.object({
  email: z.string(),
  name: z.string().nullable(),
  outcome: conversionOutcomeSchema,
  problems: z.array(z.string()),
  /** The company they are converted into, and whether it is made by this run. */
  company: z.object({ name: z.string(), isNew: z.boolean(), owner: z.boolean() }).nullable(),
  /** The account before the run: what a reversal would be written from. */
  before: z
    .object({ role: roleSchema, tenantId: z.string().nullable(), status: userStatusSchema, eventIds: z.array(z.string()) })
    .nullable(),
  events: z.array(conversionEventDtoSchema),
  invitation: conversionInvitationDtoSchema.nullable(),
})

/** What a conversion run found, and did (docs/planner-vendors.md, "The conversion task"). */
export const plannerConversionReportDtoSchema = z.object({
  applied: z.boolean(),
  /** Whether the people were found by the API's rule rather than listed. */
  found: z.boolean().default(false),
  /** Addresses left out by `exclude`. */
  excluded: z.array(z.string()).default([]),
  people: z.array(conversionPersonDtoSchema),
})

export type ConversionEventDto = z.infer<typeof conversionEventDtoSchema>
export type ConversionPersonDto = z.infer<typeof conversionPersonDtoSchema>
export type PlannerConversionReportDto = z.infer<typeof plannerConversionReportDtoSchema>

/**
 * What a venue manager is shown before marking a client as a planner, and after
 * (decision 32). The events in other organizations are counted, never named: they
 * are not this venue's to read.
 */
export const clientToPlannerDtoSchema = z.object({
  personName: z.string(),
  companyName: z.string(),
  /** The events in the manager's reach that gain them as planner. */
  events: z.array(conversionEventDtoSchema),
  /** How many more, at venues the manager does not manage. */
  otherEvents: z.number().int().nonnegative(),
  /** What stops it, worded for the manager. Empty when it can go ahead. */
  problems: z.array(z.string()),
  /** Whether they are invited: only someone who has not signed in yet is. */
  invites: z.boolean(),
  applied: z.boolean(),
  /**
   * After converting someone who is invited: whether the invitation went. A
   * failed send leaves them a planner who can be sent it again from the event.
   * Null in a preview and for someone not invited.
   */
  invitationSent: z.boolean().nullable().default(null),
})

export type ClientToPlannerDto = z.infer<typeof clientToPlannerDtoSchema>
